import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, Trophy, UserPlus, Bank, Gift } from "@phosphor-icons/react";
import { api } from "../lib/api";

const ICONS = {
    payout_approved: Bank,
    payout_rejected: Bank,
    follower_new: UserPlus,
    gift_received: Gift,
    tournament_win: Trophy,
    tournament_place: Trophy,
};
const COLORS = {
    payout_approved: "text-wta-accent",
    payout_rejected: "text-wta-primary",
    follower_new: "text-wta-secondary",
    gift_received: "text-wta-accent",
    tournament_win: "text-wta-accent",
    tournament_place: "text-white",
};

const PAGE_SIZE = 30;

export default function Notifications() {
    const [items, setItems] = useState([]);
    const [skip, setSkip] = useState(0);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (s) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/notifications?limit=${PAGE_SIZE}&skip=${s}`);
            setItems(data.items);
            setTotal(data.total);
            setSkip(s);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(0); }, [load]);

    const markAll = async () => {
        await api.post("/notifications/mark-read", { ids: null });
        load(skip);
    };

    return (
        <div className="px-4 md:px-10 py-8 max-w-3xl mx-auto">
            <div className="flex items-end justify-between">
                <div>
                    <div className="font-heading text-xs tracking-[0.4em] text-wta-muted">YOUR INBOX</div>
                    <h1 className="font-heading text-5xl md:text-6xl tracking-tight uppercase flex items-center gap-3">
                        <Bell size={44} weight="fill" className="text-wta-accent" /> Notifications
                    </h1>
                </div>
                {items.some((n) => !n.read) && (
                    <button data-testid="mark-all-read" onClick={markAll} className="btn-ghost px-4 py-2 rounded-sm text-sm font-heading uppercase tracking-wider">
                        Mark all read
                    </button>
                )}
            </div>

            <div className="mt-8 bg-wta-surface border border-wta-border rounded-sm overflow-hidden">
                {loading && items.length === 0 ? (
                    <div className="p-6 text-wta-muted text-sm text-center">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="p-12 text-center">
                        <Bell size={48} className="text-wta-muted mx-auto" />
                        <div className="mt-4 text-wta-muted">Nothing yet. Compete, post, and engage to start getting notifications.</div>
                    </div>
                ) : items.map((n, i) => {
                    const Icon = ICONS[n.type] || Bell;
                    const color = COLORS[n.type] || "text-white";
                    const inner = (
                        <div className={`flex gap-3 px-5 py-4 ${i !== items.length - 1 ? "border-b border-wta-border" : ""} ${!n.read ? "bg-white/[0.02]" : ""}`}>
                            <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-wta-surface2 border border-wta-border ${color}`}>
                                <Icon size={18} weight="fill" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium flex items-center gap-2">
                                    {n.title}
                                    {!n.read && <span className="w-2 h-2 rounded-full bg-wta-primary" />}
                                </div>
                                <div className="text-sm text-wta-muted">{n.message}</div>
                                <div className="text-xs text-wta-muted mt-1">{new Date(n.created_at).toLocaleString()}</div>
                            </div>
                        </div>
                    );
                    return n.link ? (
                        <Link key={n.id} to={n.link} data-testid={`notif-row-${n.id}`}>{inner}</Link>
                    ) : (
                        <div key={n.id} data-testid={`notif-row-${n.id}`}>{inner}</div>
                    );
                })}
            </div>

            {total > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 text-sm">
                    <div className="text-wta-muted">{skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total}</div>
                    <div className="flex gap-2">
                        <button data-testid="notif-prev" disabled={skip === 0} onClick={() => load(Math.max(0, skip - PAGE_SIZE))} className="btn-ghost px-4 py-1.5 rounded-sm text-sm font-heading uppercase tracking-wider disabled:opacity-40">Prev</button>
                        <button data-testid="notif-next" disabled={skip + PAGE_SIZE >= total} onClick={() => load(skip + PAGE_SIZE)} className="btn-ghost px-4 py-1.5 rounded-sm text-sm font-heading uppercase tracking-wider disabled:opacity-40">Next</button>
                    </div>
                </div>
            )}
        </div>
    );
}
